// Profile service for player photos and achievements
import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';

// Upload player profile photo
export const uploadProfilePhoto = async (playerId, file) => {
  if (!file) {
    throw new Error('No file provided');
  }

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Only JPEG, PNG, and WebP images are allowed');
  }

  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    throw new Error('File size must be less than 5MB');
  }

  try {
    // Create a reference to the storage location
    const fileExtension = file.name.split('.').pop();
    const fileName = `${playerId}.${fileExtension}`;
    const storageRef = ref(storage, `profile-photos/${fileName}`);

    // Upload the file
    await uploadBytes(storageRef, file);

    // Get the download URL
    const photoURL = await getDownloadURL(storageRef);

    // Update player document with photo URL
    const playerRef = doc(db, 'players', playerId);
    await updateDoc(playerRef, {
      photoURL,
      updatedAt: serverTimestamp()
    });

    return photoURL;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw new Error('Failed to upload profile photo');
  }
};

// Add an achievement/record to a player
export const addPlayerAchievement = async (playerId, achievement) => {
  const achievementRef = collection(db, 'achievements');

  const achievementData = {
    playerId,
    competitionId: achievement.competitionId,
    competitionName: achievement.competitionName,
    type: achievement.type, // 'overall_winner', 'category_winner'
    categoryId: achievement.categoryId || null,
    categoryName: achievement.categoryName || null,
    year: achievement.year || new Date().getFullYear(),
    date: achievement.date || new Date().toISOString().split('T')[0],
    notes: achievement.notes || null,
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(achievementRef, achievementData);

  return {
    id: docRef.id,
    ...achievementData
  };
};

// Get all achievements for a player
export const getPlayerAchievements = async (playerId) => {
  const achievementsRef = collection(db, 'achievements');
  const q = query(
    achievementsRef,
    where('playerId', '==', playerId),
    orderBy('year', 'desc'),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get achievements for a specific competition
export const getCompetitionAchievements = async (competitionId) => {
  const achievementsRef = collection(db, 'achievements');
  const q = query(
    achievementsRef,
    where('competitionId', '==', competitionId)
  );

  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update player display name
export const updatePlayerProfile = async (playerId, updates) => {
  const playerRef = doc(db, 'players', playerId);
  await updateDoc(playerRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};
